'use client';
import { STORAGE_CONFIG } from '@/config/app';
import { logger } from '@/lib/logger';

/**
 * Navigation Visibility Context
 *
 * Provides global state management for navigation item visibility with localStorage persistence.
 * Controls which navigation menu items appear in the side navigation based on user preferences.
 *
 * @module NavigationVisibilityContext
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

/**
 * State interface tracking visibility for each navigation item
 */
interface NavigationVisibilityState {
  'aggrid-demo': boolean;
  'highcharts-demo': boolean;
  map: boolean;
  bigquery: boolean;
}

/**
 * Context API interface for navigation visibility management
 */
interface NavigationVisibilityContextType {
  visibility: NavigationVisibilityState;
  toggleVisibility: (itemId: keyof NavigationVisibilityState) => void;
  setVisibility: (itemId: keyof NavigationVisibilityState, visible: boolean) => void;
}

const NavigationVisibilityContext = createContext<NavigationVisibilityContextType | undefined>(
  undefined
);

const STORAGE_KEY = STORAGE_CONFIG.keys.navVisibility;

const DEFAULT_VISIBILITY: NavigationVisibilityState = {
  'aggrid-demo': true,
  'highcharts-demo': true,
  map: true,
  bigquery: true,
};

export function NavigationVisibilityProvider({ children }: { children: React.ReactNode }) {
  const [visibility, setVisibilityState] = useState<NavigationVisibilityState>(DEFAULT_VISIBILITY);
  const [mounted, setMounted] = useState(false);

  // Load visibility preferences from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const savedVisibility = localStorage.getItem(STORAGE_KEY);

    if (savedVisibility) {
      try {
        const parsed = JSON.parse(savedVisibility);
        setVisibilityState({ ...DEFAULT_VISIBILITY, ...parsed });
      } catch (error) {
        logger.error('Failed to parse navigation visibility settings', 'NavigationVisibility', error);
      }
    }
  }, []);

  // Save visibility preferences to localStorage when they change
  useEffect(() => {
    if (mounted) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibility));
    }
  }, [visibility, mounted]);

  const toggleVisibility = (itemId: keyof NavigationVisibilityState) => {
    setVisibilityState((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const setVisibility = (itemId: keyof NavigationVisibilityState, visible: boolean) => {
    setVisibilityState((prev) => ({
      ...prev,
      [itemId]: visible,
    }));
  };

  const contextValue = useMemo(
    () => ({
      visibility,
      toggleVisibility,
      setVisibility,
    }),
    [visibility]
  );

  // Prevent flash of unstyled content
  if (!mounted) {
    return null;
  }

  return (
    <NavigationVisibilityContext.Provider value={contextValue}>
      {children}
    </NavigationVisibilityContext.Provider>
  );
}

export function useNavigationVisibility() {
  const context = useContext(NavigationVisibilityContext);
  if (context === undefined) {
    throw new Error('useNavigationVisibility must be used within a NavigationVisibilityProvider');
  }
  return context;
}
